/**
 * cellRules.js — pure predicates for the cell panel's edit gating.
 *
 * `cell_workflow`, `workspace`, and `workspace_mode` are STRUCTURAL choices: what the orchestrator
 * dispatches a run with. They are editable in `draft` and nowhere else.
 *
 * Draft is the only state in which nothing can act on the cell — the heartbeat's query skips
 * `draft` and `inactive` outright. From `planned` onward the tick reconciles the cell every minute
 * and may promote and dispatch it at any moment, so "still editable in planned/ready" was a race
 * with a one-minute window: a workspace changed in the same tick that dispatched would be read by
 * nobody, and the run would carry the previous value while the panel showed the new one. Freezing
 * at the point work becomes possible removes the window rather than narrowing it.
 */

/**
 * Whether the cell's structural props (cell_workflow / workspace / workspace_mode) are still
 * editable — true in `draft` only.
 * @param {{ props?: { status?: string } }} cell
 * @returns {boolean}
 */
export function isStructureEditable(cell) {
  return (((cell && cell.props) || {}).status || 'draft') === 'draft';
}

/**
 * Has this cell left draft — i.e. can the orchestrator act on it? The exact negation of
 * isStructureEditable; kept as its own export so call sites read as intent rather than as a double
 * negative.
 * @param {{ props?: { status?: string } }} cell
 * @returns {boolean}
 */
export function cellHasRun(cell) {
  return !isStructureEditable(cell);
}

/**
 * Which of the cell's three subsections stand open when you arrive at it.
 *
 * A draft is a cell being WRITTEN, and all three sections are the writing: what it is, what it
 * runs, where it runs. Anything else is a cell being watched, and then the same three are a
 * settled record — worth having, not worth the screen. Details and Workspace fold away, and the
 * space goes to the meta-thread below, which is the part that is still moving.
 *
 * Cell Workflow stays open either way. It is the only one of the three whose value is a GRAPH
 * rather than a line of text, and it answers the question somebody watching a running cell is
 * actually asking — which step is it on, and what comes after.
 *
 * A default, not a rule: a person's own toggle outranks it until the answer changes underneath
 * them (a different cell, or this one leaving draft).
 *
 * @param {Object} cell
 * @param {boolean} canEdit - false on a committed revision, a closed session, or no write access
 * @returns {{ details: boolean, workflow: boolean, workspace: boolean }}
 */
export function defaultSectionsExpanded(cell, canEdit) {
  const editable = isStructureEditable(cell) && canEdit !== false;
  return { details: editable, workflow: true, workspace: editable };
}
